import { useCallback } from 'react';

import { AppointmentPractitioner } from '@dreryk/egabinet-appointment-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { StyledFlex } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import {
   GroupSessionsFormBean,
   GroupSessionsFormController,
   GroupSessionsFormInitializer,
} from './GroupSessionsForm.types';
import { FourColumnsGrid, StyledForm, ThreeColumnsGrid } from './GroupSessionsForm.styled';
import { GroupSessionsNameField } from './fields/GroupSessionsNameField';
import { GroupSessionsTypeField } from './fields/GroupSessionsTypeField';
import { GroupSessionsMinParticipantsField } from './fields/GroupSessionsMinParticipantsField';
import { GroupSessionsMaxParticipantsField } from './fields/GroupSessionsMaxParticipantsField';
import { GroupSessionsAgeRestrictionEnabledField } from './fields/GroupSessionsAgeRestrictionEnabledField';
import { GroupSessionsMinAgeField } from './fields/GroupSessionsMinAgeField';
import { withAgeRestriction } from './GroupSessionsForm.utils';
import { GroupSessionsMaxAgeField } from './fields/GroupSessionsMaxAgeField';
import { GroupSessionsPractitionersField } from './fields/GroupSessionsPractitionersField';
import { GroupSessionsNoteField } from './fields/GroupSessionsNoteField';
import { GroupSessionsReceptionServiceField } from './fields/GroupSessionsReceptionServiceField';
import { GroupSessionsMedicalServiceField } from './fields/GroupSessionsMedicalServiceField';
import { GroupSessionsReceptionServiceNoification } from './fields/GroupSessionsReceptionServiceNoification';
import { GroupSessionsMedicalServiceNoification } from './fields/GroupSessionsMedicalServiceNoification';

type GroupSessionsFormRenderProps = Pick<GroupSessionsFormController, 'handleSubmit' | 'formState' | 'trigger'> & {
   reset: VoidFunction;
};

type GroupSessionsFormProps = {
   readonly children: (renderProps: GroupSessionsFormRenderProps) => JSX.Element;
   readonly initializer: GroupSessionsFormInitializer;
   readonly practitioners: AppointmentPractitioner[];
};

export const GroupSessionsForm = (props: GroupSessionsFormProps): JSX.Element => {
   const controller =
      // eslint-disable-next-line react-hooks/exhaustive-deps
      useFormController<GroupSessionsFormBean>(props.initializer());

   const handleReset = useCallback<VoidFunction>(() => {
      controller.reset(props.initializer());
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [controller.reset, props.initializer]);

   return (
      <StyledForm noValidate autoComplete={'off'}>
         <GroupSessionsNameField register={controller.register} />
         <ThreeColumnsGrid>
            <GroupSessionsTypeField register={controller.register} />
            <GroupSessionsMinParticipantsField
               register={controller.register}
               getValue={controller.getValue}
               setValue={controller.setValue}
            />
            <GroupSessionsMaxParticipantsField
               register={controller.register}
               getValue={controller.getValue}
               setValue={controller.setValue}
            />
         </ThreeColumnsGrid>
         <GroupSessionsAgeRestrictionEnabledField register={controller.register} />
         {withAgeRestriction(controller) && (
            <FourColumnsGrid>
               <GroupSessionsMinAgeField
                  register={controller.register}
                  getValue={controller.getValue}
                  setValue={controller.setValue}
               />
               <GroupSessionsMaxAgeField
                  register={controller.register}
                  getValue={controller.getValue}
                  setValue={controller.setValue}
               />
            </FourColumnsGrid>
         )}
         <GroupSessionsPractitionersField register={controller.register} practitioners={props.practitioners} />
         {protectFeature(
            'searchingPrivateMedicalServices',
            <StyledFlex direction={'column'} gap={'8px'}>
               <GroupSessionsReceptionServiceField register={controller.register} />
               <GroupSessionsReceptionServiceNoification
                  selectedReceptionService={controller.watch('receptionService')}
                  selectedPractitioners={controller.watch('practitioners.value')}
               />
            </StyledFlex>
         )}
         {protectFeature(
            'searchingPrivateMedicalServices',
            <StyledFlex direction={'column'} gap={'8px'}>
               <GroupSessionsMedicalServiceField register={controller.register} />
               <GroupSessionsMedicalServiceNoification
                  selectedMedicalService={controller.watch('medicalService')}
                  selectedPractitioners={controller.watch('practitioners.value')}
               />
            </StyledFlex>
         )}
         <GroupSessionsNoteField register={controller.register} defaultValue={controller.watch('note')} />
         {props.children({
            handleSubmit: controller.handleSubmit,
            formState: controller.formState,
            trigger: controller.trigger,
            reset: handleReset,
         })}
      </StyledForm>
   );
};
